import { Component, Input, Output, EventEmitter, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export interface DateValue {
  date: Date | null;
  formatted?: string;
}

export interface DateConfig {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  showClearButton?: boolean;
  format?: 'DD/MM/YYYY' | 'MM/DD/YYYY';
  max?: string | null;
}

@Component({
  selector: 'app-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule
  ]
})
export class DateFilterComponent implements OnInit {
  @Input() config: DateConfig = {
    label: 'Date',
    placeholder: 'Select date',
    disabled: false,
    showClearButton: true,
    format: 'DD/MM/YYYY',
    max: new Date().toISOString().split('T')[0] // Por defecto, la fecha máxima es hoy
  };

  @Input() initialValue: Date | null = null;
  @Input() userLocale: string = 'es';
  @Input() isLoading: boolean = false;
  @Input() labelClass: string = '';
  @Input() hasError: boolean = false;

  @Output() dateChanged = new EventEmitter<Date | null>();
  @Output() cleared = new EventEmitter<void>();

  selectedDate = signal<Date | null>(null);
  dateInput = signal<string>('');
  todayString: string = new Date().toISOString().split('T')[0]; // formato YYYY-MM-DD

  constructor(private translateService: TranslateService) {}

  ngOnInit() {
    if (this.initialValue) {
      this.selectedDate.set(this.initialValue);
      this.dateInput.set(this.formatDate(this.initialValue));
    }
  }

  private resolvedFormat(): 'DD/MM/YYYY' | 'MM/DD/YYYY' {
    return this.config?.format ?? 'DD/MM/YYYY';
  }

  /**
   * Formatea una fecha a string según el formato configurado.
   */
  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    switch (this.resolvedFormat()) {
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'DD/MM/YYYY':
      default:
        return `${day}/${month}/${year}`;
    }
  }

  private parseDateString(dateString: string): Date | null {
    const trimmedValue = dateString.trim();
    if (!trimmedValue) {
      return null;
    }

    const match = trimmedValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (!match) {
      return null;
    }

    const [, firstPart, secondPart, yearPart] = match;
    const year = Number(yearPart);
    const first = Number(firstPart);
    const second = Number(secondPart);

    const month = this.resolvedFormat() === 'MM/DD/YYYY' ? first : second;
    const day = this.resolvedFormat() === 'MM/DD/YYYY' ? second : first;

    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    if (
      parsedDate.getFullYear() !== year ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getDate() !== day
    ) {
      return null;
    }

    if (this.config.max) {
      const maxDate = new Date(`${this.config.max}T00:00:00`);
      if (!isNaN(maxDate.getTime()) && parsedDate.getTime() > maxDate.getTime()) {
        return null;
      }
    }

    return parsedDate;
  }

  /**
   * Maneja el cambio desde el input de fecha.
   */
  onDateInput(event: Event) {
    const target = event.target as HTMLInputElement;
    this.dateInput.set(target.value);
  }

  onDateBlur() {
    const rawValue = this.dateInput();
    if (!rawValue.trim()) {
      this.clearDateRange();
      return;
    }

    const date = this.parseDateString(rawValue);
    if (date) {
      this.selectedDate.set(date);
      this.dateInput.set(this.formatDate(date));
      this.dateChanged.emit(date);
    } else {
      this.selectedDate.set(null);
      this.dateChanged.emit(null);
    }
  }

  onNativePickerChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    const date = this.fromNativePickerFormat(value);

    if (date) {
      this.selectedDate.set(date);
      this.dateInput.set(this.formatDate(date));
      this.dateChanged.emit(date);
    } else {
      this.clearDateRange();
    }
  }

  /**
   * Limpia la fecha seleccionada.
   */
  clearDateRange() {
    this.selectedDate.set(null);
    this.dateInput.set('');
    this.dateChanged.emit(null);
    this.cleared.emit();
  }

  /**
   * Verifica si hay una fecha seleccionada.
   */
  selectedDateExists(): boolean {
    return !!this.selectedDate();
  }

  /**
   * Valor actual como string (para input).
   */
  inputValue(): string {
    return this.dateInput();
  }

  nativePickerValue(): string {
    const selectedDate = this.selectedDate();
    return selectedDate ? this.toNativePickerFormat(selectedDate) : '';
  }

  /**
   * Traduce la etiqueta si existe en el archivo i18n.
   */
  getTranslatedLabel(label: string): string {
    return this.translateService.instant(label) || label;
  }

  /**
   * Devuelve la fecha actual seleccionada.
   */
  selectedDateValue(): Date | null {
    return this.selectedDate();
  }

  private toNativePickerFormat(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private fromNativePickerFormat(dateString: string): Date | null {
    const date = new Date(`${dateString}T00:00:00`);
    return isNaN(date.getTime()) ? null : date;
  }
}
